/**
 * SystemLanguageService for managing system-wide language settings
 * Controls the language of LLM outputs (not interface language)
 */
import axios from 'axios';
import { API_BASE_URL } from '../config';
import cacheService from './cacheService';
import { createErrorLogger, ErrorType } from '../utils/logger';

const logger = createErrorLogger('SystemLanguageService');

// Cache key for system language
const SYSTEM_LANGUAGE_CACHE_KEY = 'system_language';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

class SystemLanguageService {
  /**
   * Get current system language setting
   * @returns {Promise<object>} System language result with language and timestamp
   */
  async getSystemLanguage() {
    logger.info('getSystemLanguage', 'Fetching current system language setting');

    try {
      // Check cache first
      const cachedLanguage = cacheService.get(SYSTEM_LANGUAGE_CACHE_KEY);
      if (cachedLanguage) {
        logger.info('getSystemLanguage', 'Returning cached system language', { language: cachedLanguage.language });
        return cachedLanguage;
      }

      const response = await axios.get(`${API_BASE_URL}/system/language`);

      if (response.data && response.data.result) {
        const languageData = response.data.result;

        // Cache the result
        cacheService.set(SYSTEM_LANGUAGE_CACHE_KEY, languageData, CACHE_DURATION_MS);

        logger.info('getSystemLanguage', 'System language retrieved successfully', {
          language: languageData.language,
          timestamp: languageData.timestamp
        });

        return languageData;
      } else {
        throw new Error('Invalid response format from system language endpoint');
      }
    } catch (error) {
      logger.error('getSystemLanguage', 'Failed to get system language', {
        error: error.message,
        status: error.response?.status,
        responseData: error.response?.data
      }, ErrorType.NETWORK_ERROR);
      throw error;
    }
  }

  /**
   * Set system language setting (admin only)
   * @param {string} language - Language to set ('english' or 'french')
   * @returns {Promise<object>} Update result with language, updated_by, and timestamp
   */
  async setSystemLanguage(language) {
    logger.info('setSystemLanguage', 'Attempting to set system language', { language });

    // Validate language parameter
    if (!language || !['english', 'french'].includes(language)) {
      const error = new Error(`Invalid language parameter: ${language}. Must be 'english' or 'french'.`);
      logger.error('setSystemLanguage', 'Invalid language parameter', { language }, ErrorType.VALIDATION_ERROR);
      throw error;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/system/language`, {
        language: language
      });

      if (response.data && response.data.result) {
        const updateResult = response.data.result;

        // Clear cache to force fresh fetch next time
        cacheService.clear(SYSTEM_LANGUAGE_CACHE_KEY);

        logger.info('setSystemLanguage', 'System language updated successfully', {
          language: updateResult.language,
          updated_by: updateResult.updated_by,
          timestamp: updateResult.timestamp
        });

        return updateResult;
      } else {
        throw new Error('Invalid response format from system language update endpoint');
      }
    } catch (error) {
      logger.error('setSystemLanguage', 'Failed to set system language', {
        language,
        error: error.message,
        status: error.response?.status,
        responseData: error.response?.data
      }, ErrorType.NETWORK_ERROR);

      // Handle specific error cases
      if (error.response?.status === 403) {
        throw new Error('Admin privileges required to change system language setting');
      } else if (error.response?.status === 400) {
        throw new Error(error.response.data?.detail || 'Invalid language parameter');
      } else if (error.response?.status === 401) {
        throw new Error('Authentication required to change system language setting');
      } else {
        throw new Error(`Failed to update system language: ${error.message}`);
      }
    }
  }

  /**
   * Clear cached system language data
   */
  clearCache() {
    logger.info('clearCache', 'Clearing system language cache');
    cacheService.clear(SYSTEM_LANGUAGE_CACHE_KEY);
  }
}

// Export singleton instance
const systemLanguageService = new SystemLanguageService();
export default systemLanguageService;